import {
  ExternalE2EEKeyProvider,
  isE2EESupported,
  type LocalVideoTrack,
  type Participant,
  type RemoteTrack,
  Room,
  RoomEvent,
  supportsAudioOutputSelection,
  Track,
  type VideoTrack,
} from "livekit-client";
import { detectPlatform, type PlatformKind } from "../platform";
import { createE2EEWorker } from "./worker";

// One LiveKit room per call, always end-to-end encrypted. The media key comes from the chat's
// encrypted messages and goes only into the key provider, which hands it to the worker. LiveKit
// sees the room token and encrypted frames, never the key.

export const UNSUPPORTED =
  "This browser cannot encrypt call audio and video end to end, so Rhema will not join the call. Use a recent version of Chrome, Edge, Firefox or Safari.";

/** The same refusal in the desktop app, whose webview comes with the operating system. */
export const UNSUPPORTED_DESKTOP =
  "Rhema for desktop cannot encrypt call audio and video end to end on this computer, so it will not join the call. On a Mac, update to macOS 12.3 or later. You can also call from Rhema on the web in a recent version of Chrome, Edge, Firefox or Safari.";

/**
 * The same refusal in the iPhone app. WKWebView encrypts call frames from iOS 15.4, which has
 * RTCRtpScriptTransform.
 */
export const UNSUPPORTED_IOS =
  "Rhema cannot encrypt call audio and video end to end on this iPhone, so it will not join the call. Update to iOS 15.4 or later and try again.";

/** The same refusal in the Android app, whose webview is Android System WebView. */
export const UNSUPPORTED_ANDROID =
  "Rhema cannot encrypt call audio and video end to end on this phone, so it will not join the call. Update Android System WebView and Chrome from Google Play and try again.";

type CapacitorHost = { Capacitor?: { getPlatform?: () => string } };

/** What to tell the person when this device cannot encrypt calls. */
export function unsupportedMessage(
  kind: PlatformKind = detectPlatform(),
  os: string = (globalThis as CapacitorHost).Capacitor?.getPlatform?.() ?? "",
): string {
  if (kind === "tauri") return UNSUPPORTED_DESKTOP;
  if (kind === "capacitor") return os === "ios" ? UNSUPPORTED_IOS : UNSUPPORTED_ANDROID;
  return UNSUPPORTED;
}

export type DeviceKind = "audioinput" | "videoinput" | "audiooutput";

export type Tile = {
  identity: string;
  name: string;
  local: boolean;
  speaking: boolean;
  micOn: boolean;
  cameraOn: boolean;
  handRaised: boolean;
  camera?: VideoTrack;
  screen?: VideoTrack;
};

export type RoomSnapshot = {
  status: "connecting" | "connected" | "reconnecting" | "disconnected";
  /** True only once LiveKit reports that this device encrypts what it sends. */
  encrypted: boolean;
  tiles: Tile[];
  micOn: boolean;
  cameraOn: boolean;
  screenOn: boolean;
  handRaised: boolean;
  /** A plain explanation of a device or permission problem. */
  problem: string | null;
};

const HAND_TOPIC = "rhema.hand";

export function e2eeSupported(): boolean {
  try {
    return isE2EESupported();
  } catch {
    return false;
  }
}

/** Explains a getUserMedia or getDisplayMedia failure in plain words. */
export function describeMediaError(err: unknown, what: "microphone" | "camera" | "screen"): string {
  const name = (err as { name?: string } | null)?.name ?? "";
  if (what === "screen") {
    if (name === "NotAllowedError") return "Screen sharing was cancelled or is blocked for this site.";
    return "Rhema could not share your screen.";
  }
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError")
    return `Rhema cannot use your ${what} because access was denied. Allow ${what} access for this site in your browser settings, then try again.`;
  if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError")
    return `No ${what} was found. Connect one and try again.`;
  if (name === "NotReadableError" || name === "TrackStartError")
    return `Your ${what} is in use by another app. Close that app and try again.`;
  return `Rhema could not start your ${what}.`;
}

type RoomFactory = (options: ConstructorParameters<typeof Room>[0]) => Room;

export class CallRoom {
  room: Room | null = null;
  snapshot: RoomSnapshot = {
    status: "connecting",
    encrypted: false,
    tiles: [],
    micOn: false,
    cameraOn: false,
    screenOn: false,
    handRaised: false,
    problem: null,
  };
  private listeners = new Set<() => void>();
  private hands = new Set<string>();
  private audio = new Map<string, HTMLMediaElement>();
  private facing: "user" | "environment" = "user";
  private closed = false;

  constructor(
    private readonly createRoom: RoomFactory = (o) => new Room(o),
    private readonly createWorker: () => Worker = createE2EEWorker,
  ) {}

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.snapshot;

  private patch(next: Partial<RoomSnapshot>) {
    this.snapshot = { ...this.snapshot, ...next };
    for (const l of this.listeners) l();
  }

  async connect(opts: { url: string; token: string; key: string; video: boolean }): Promise<void> {
    // Never fall back to an unencrypted call.
    if (!e2eeSupported()) throw new Error(UNSUPPORTED);
    const keyProvider = new ExternalE2EEKeyProvider();
    await keyProvider.setKey(opts.key);
    const room = this.createRoom({
      adaptiveStream: true,
      dynacast: true,
      encryption: { keyProvider, worker: this.createWorker() },
    });
    this.room = room;
    this.bind(room);
    await room.setE2EEEnabled(true);
    await room.connect(opts.url, opts.token);
    if (this.closed) {
      await room.disconnect();
      return;
    }
    this.refresh();
    await this.setMic(true);
    if (opts.video) await this.setCamera(true);
    await room.startAudio().catch(() => undefined);
  }

  private bind(room: Room) {
    const refresh = () => this.refresh();
    for (const e of [
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.TrackPublished,
      RoomEvent.TrackUnpublished,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished,
      RoomEvent.ActiveSpeakersChanged,
      RoomEvent.ParticipantEncryptionStatusChanged,
      RoomEvent.ConnectionStateChanged,
    ])
      room.on(e, refresh);
    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub, participant: Participant) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.style.display = "none";
        document.body.appendChild(el);
        this.audio.set(`${participant.identity}:${track.sid}`, el);
      }
      this.refresh();
    });
    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub, participant: Participant) => {
      const id = `${participant.identity}:${track.sid}`;
      const el = this.audio.get(id);
      if (el) {
        track.detach(el);
        el.remove();
        this.audio.delete(id);
      }
      this.refresh();
    });
    room.on(RoomEvent.ParticipantConnected, () => {
      // Tell newcomers about a hand that is already up.
      if (this.snapshot.handRaised) void this.sendHand(true);
    });
    room.on(RoomEvent.ParticipantDisconnected, (p: Participant) => {
      this.hands.delete(p.identity);
      this.refresh();
    });
    room.on(
      RoomEvent.DataReceived,
      (payload: Uint8Array, participant?: Participant, _k?: unknown, topic?: string) => {
        if (topic !== HAND_TOPIC || !participant) return;
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload)) as { raised?: boolean };
          if (msg.raised) this.hands.add(participant.identity);
          else this.hands.delete(participant.identity);
          this.refresh();
        } catch {
          // Ignore anything that is not ours.
        }
      },
    );
    room.on(RoomEvent.MediaDevicesError, (err: Error) =>
      this.patch({ problem: describeMediaError(err, "microphone") }),
    );
    room.on(RoomEvent.Disconnected, () => {
      this.cleanupAudio();
      this.patch({ status: "disconnected" });
    });
  }

  private tileOf(p: Participant, local: boolean): Tile {
    const cam = p.getTrackPublication(Track.Source.Camera);
    const mic = p.getTrackPublication(Track.Source.Microphone);
    const screen = p.getTrackPublication(Track.Source.ScreenShare);
    const camOn = Boolean(cam?.track && !cam.isMuted);
    return {
      identity: p.identity,
      name: p.name || p.identity,
      local,
      speaking: p.isSpeaking,
      micOn: Boolean(mic?.track && !mic.isMuted),
      cameraOn: camOn,
      handRaised: local ? this.snapshot.handRaised : this.hands.has(p.identity),
      camera: camOn ? (cam?.track as VideoTrack | undefined) : undefined,
      screen: screen?.track && !screen.isMuted ? (screen.track as VideoTrack) : undefined,
    };
  }

  refresh(): void {
    const room = this.room;
    if (!room) return;
    const local = room.localParticipant;
    const tiles = [
      ...[...room.remoteParticipants.values()].map((p) => this.tileOf(p, false)),
      this.tileOf(local, true),
    ];
    const state = room.state as string;
    this.patch({
      status:
        state === "connected"
          ? "connected"
          : state === "reconnecting" || state === "signalReconnecting"
            ? "reconnecting"
            : this.snapshot.status === "disconnected"
              ? "disconnected"
              : "connecting",
      encrypted: room.isE2EEEnabled,
      tiles,
      micOn: local.isMicrophoneEnabled,
      cameraOn: local.isCameraEnabled,
      screenOn: local.isScreenShareEnabled,
    });
  }

  // Controls

  async setMic(on: boolean): Promise<void> {
    try {
      await this.room?.localParticipant.setMicrophoneEnabled(on);
      if (on && this.snapshot.problem?.includes("microphone")) this.patch({ problem: null });
    } catch (err) {
      this.patch({ problem: describeMediaError(err, "microphone") });
    }
    this.refresh();
  }

  async setCamera(on: boolean): Promise<void> {
    try {
      await this.room?.localParticipant.setCameraEnabled(on, on ? { facingMode: this.facing } : undefined);
      if (on && this.snapshot.problem?.includes("camera")) this.patch({ problem: null });
    } catch (err) {
      this.patch({ problem: describeMediaError(err, "camera") });
    }
    this.refresh();
  }

  async setScreen(on: boolean): Promise<void> {
    try {
      await this.room?.localParticipant.setScreenShareEnabled(on);
    } catch (err) {
      this.patch({ problem: describeMediaError(err, "screen") });
    }
    this.refresh();
  }

  async flipCamera(): Promise<void> {
    const pub = this.room?.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track as LocalVideoTrack | undefined;
    if (!track) return;
    this.facing = this.facing === "user" ? "environment" : "user";
    try {
      await track.restartTrack({ facingMode: this.facing });
    } catch (err) {
      this.patch({ problem: describeMediaError(err, "camera") });
    }
    this.refresh();
  }

  async setHand(raised: boolean): Promise<void> {
    this.patch({ handRaised: raised });
    await this.sendHand(raised);
    this.refresh();
  }

  private async sendHand(raised: boolean) {
    const data = new TextEncoder().encode(JSON.stringify({ raised }));
    await this.room?.localParticipant
      .publishData(data, { reliable: true, topic: HAND_TOPIC })
      .catch(() => undefined);
  }

  clearProblem(): void {
    this.patch({ problem: null });
  }

  async devices(kind: DeviceKind): Promise<MediaDeviceInfo[]> {
    if (kind === "audiooutput" && !supportsAudioOutputSelection()) return [];
    try {
      return await Room.getLocalDevices(kind, false);
    } catch {
      return [];
    }
  }

  activeDevice(kind: DeviceKind): string | undefined {
    return this.room?.getActiveDevice(kind);
  }

  async switchDevice(kind: DeviceKind, deviceId: string): Promise<void> {
    try {
      await this.room?.switchActiveDevice(kind, deviceId);
    } catch (err) {
      this.patch({ problem: describeMediaError(err, kind === "videoinput" ? "camera" : "microphone") });
    }
  }

  async disconnect(): Promise<void> {
    this.closed = true;
    this.cleanupAudio();
    await this.room?.disconnect().catch(() => undefined);
    this.patch({ status: "disconnected" });
  }

  private cleanupAudio() {
    for (const el of this.audio.values()) el.remove();
    this.audio.clear();
  }
}

/** Flipping needs a camera that can face both ways, which in practice means a phone or tablet. */
export async function canFlipCamera(): Promise<boolean> {
  const md = globalThis.navigator?.mediaDevices;
  if (!md?.getSupportedConstraints?.().facingMode || !md.enumerateDevices) return false;
  const devices = await md.enumerateDevices().catch(() => []);
  return devices.filter((d) => d.kind === "videoinput").length > 1;
}

export function canShareScreen(): boolean {
  return typeof globalThis.navigator?.mediaDevices?.getDisplayMedia === "function";
}
